import React, { useRef, useState } from 'react';
import './Build.css'; // スタイルシートのパスを調整してください

function ImageUploader({ onRecognitionComplete }) {
  const [isProcessing, setIsProcessing] = useState(false);
  const [selectedSource, setSelectedSource] = useState(null);
  const [recognitionMessage, setRecognitionMessage] = useState('');
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  const [images, setImages] = useState({}); // 認識結果の画像
  const [additionalImages, setAdditionalImages] = useState({}); // ユーザーが追加した画像
  const allImages = { ...images, ...additionalImages };
  const hasUploadedSource = selectedSource !== null;

  const handleImageUpload = (source, e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedSource(source);
      handleRecognition(file);
    }
    e.target.value = '';
  };

  const handleRecognition = async (file) => {
    if (!file) {
      alert('先に画像をアップロードしてください。');
      return;
    }

    setIsProcessing(true);
    setRecognitionMessage('');

    const formData = new FormData();
    formData.append('image', file);

    try {
      const baseURL = process.env.REACT_APP_API_URL || '';
      const response = await fetch(`${ baseURL }/recognize`, {
        method: 'POST',
        body: formData,
      });
      const result = await response.json();
      // 認識結果の画像を状態に保存
      const recognizedImages = result.images || {};
      const recognizedDigitCount = Object.keys(recognizedImages).filter((key) => /^\d+$/.test(key)).length;
      setImages(recognizedImages);
      setRecognitionMessage(
        recognizedDigitCount > 0
          ? `読み取り結果を下で確認できます。足りない数字は各マスから追加できます。`
          : '自動で読み取れた数字がありませんでした。下のマスから個別に追加できます。'
      );

      // 認識結果を親コンポーネントに渡す
      if (onRecognitionComplete) {
        onRecognitionComplete({ ...result, images: recognizedImages });
      }
    } catch (error) {
      console.error('認識に失敗しました:', error);
      setImages({});
      setRecognitionMessage('読み取りに失敗しました。別の写真でお試しください。');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleUpload = (digit, e) => {
    const file = e.target.files[0];
    if (file) {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;

        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);

        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const data = imageData.data;

        // 背景透過処理
        const threshold = 180; // 輝度の閾値
        for (let i = 0; i < data.length; i += 4) {
          const brightness =
            0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
          if (brightness > threshold) {
            data[i + 3] = 0; // 透明にする
          }
        }
        ctx.putImageData(imageData, 0, 0);

        const src = canvas.toDataURL();
        setAdditionalImages((prev) => ({ ...prev, [digit]: src }));
      };
      img.src = URL.createObjectURL(file);
    }
  };

  const openDigitUpload = (digit) => {
    const input = document.getElementById(`upload-${digit}`);
    if (input) {
      input.click();
    }
  };

  return (
    <section className="numberimage">
      <div className="numberimage-panel">
        <p className="month-grid-step">step02</p>
        <h4 className="numberimage-title">
          0〜9までの手書き写真を
          <br />
          アップロードしよう
        </h4>
        <p className="numberimage-description">
          見本のように、1つ1つの文字を離して、なるべくはっきり映るようにしましょう。
        </p>

        <div className="numberimage-sample" aria-hidden="true">
          <div className="numberimage-sample-badge">見本画像</div>
          <img
            src="/media/mihon.jpg"
            alt=""
            className="numberimage-sample-image"
          />
        </div>

        {/* ファイル入力要素を非表示で追加 */}
        <input
          type="file"
          accept="image/*"
          onChange={(e) => handleImageUpload('album', e)}
          ref={fileInputRef}
          style={{ display: 'none' }}
        />

        {/* カメラ入力要素を非表示で追加 */}
        <input
          type="file"
          accept="image/*"
          capture="environment"
          onChange={(e) => handleImageUpload('camera', e)}
          ref={cameraInputRef}
          style={{ display: 'none' }}
        />

        <div className="numberimage-actions">
          <button
            type="button"
            className={`month-grid-upload-button numberimage-action-button${selectedSource === 'album' ? ' is-selected' : ''}`}
            onClick={() => fileInputRef.current && fileInputRef.current.click()}
          >
            <span className="month-grid-upload-main">
              <span>{selectedSource === 'album' ? '選択済み' : '写真を選択'}</span>
              <img
                src={selectedSource === 'album' ? '/media/icon-check.svg' : '/media/icon-upload.svg'}
                alt=""
                aria-hidden="true"
                className="month-grid-upload-icon-image"
              />
            </span>
            {selectedSource === 'album' && (
              <span className="month-grid-upload-sub">写真を選び直したいときはタップ</span>
            )}
          </button>

          <button
            type="button"
            className={`month-grid-upload-button numberimage-action-button${selectedSource === 'camera' ? ' is-selected' : ''}`}
            onClick={() => cameraInputRef.current && cameraInputRef.current.click()}
          >
            <span className="month-grid-upload-main">
              <span>{selectedSource === 'camera' ? '撮影済み' : 'カメラで撮る'}</span>
              <img
                src={selectedSource === 'camera' ? '/media/icon-check.svg' : '/media/icon-camera.svg'}
                alt=""
                aria-hidden="true"
                className="month-grid-upload-icon-image"
              />
            </span>
            {selectedSource === 'camera' && (
              <span className="month-grid-upload-sub">写真を撮り直したいときはタップ</span>
            )}
          </button>
        </div>

        {isProcessing && <p className="numberimage-status">認識中...</p>}
      </div>

      {hasUploadedSource && (
        <div className="numberimage-results">
          <p className="numberimage-results-title">読み取り結果</p>
          {recognitionMessage && <p className="numberimage-results-message">{recognitionMessage}</p>}
          <div className="number_list">
            {Array.from({ length: 10 }, (_, i) => i).map((digit) => (
              <div
                key={digit}
                className="digit-container"
                onClick={() => openDigitUpload(digit)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    openDigitUpload(digit);
                  }
                }}
                role="button"
                tabIndex={0}
              >
                <p>{digit}</p>
                {allImages[digit] ? (
                  <img
                    src={allImages[digit]}
                    alt={`number-${digit}`}
                    width={50}
                    className="digit-image"
                  />
                ) : (
                  <div className="upload-placeholder">
                    <span>+</span>
                  </div>
                )}
                <input
                  type="file"
                  accept="image/*"
                  id={`upload-${digit}`}
                  className="file-input"
                  onChange={(e) => handleUpload(digit, e)}
                  style={{ display: 'none' }}
                />
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}

export default ImageUploader;
